import { Temporal } from '@js-temporal/polyfill';
import { z } from 'zod';

import { calendarZone, dateSchema, localDay } from './calendar';
import type { RecordData } from './model';

export const learningLimitMs = 30 * 60_000;
export const learningNewLimit = 5;

export const cardContentSchema = z.object({
  question: z.string().trim().min(1).max(2000),
  answer: z.string().trim().min(1).max(8000),
  additions: z.string().trim().max(4000),
});

export const learningDraftRequestSchema = z.object({
  source: z.string().trim().min(1).max(12000),
  reverse: z.boolean(),
});

export const learningDraftSchema = z.object({ cards: z.array(cardContentSchema).min(1).max(8) });

export const learningCardSchema = cardContentSchema.extend({
  source: z.string().trim().max(12000),
  noteId: z.string().uuid().nullable(),
  status: z.enum(['active', 'suspended', 'retired']),
  approvedAt: z.string().datetime(),
  introducedAt: z.string().datetime().nullable(),
  lastReviewedAt: z.string().datetime().nullable(),
  dueAt: z.string().datetime(),
  intervalDays: z.number().nonnegative().max(3650),
  ease: z.number().min(1.3).max(3),
  reviews: z.number().int().nonnegative(),
  lapses: z.number().int().nonnegative(),
});

export const learningSessionSchema = z
  .object({
    day: dateSchema,
    startedAt: z.string().datetime(),
    deadline: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    reviewed: z.number().int().nonnegative(),
  })
  .refine(value => {
    const start = Date.parse(value.startedAt),
      deadline = Date.parse(value.deadline);

    return (
      deadline > start &&
      deadline - start <= learningLimitMs &&
      (value.endedAt === null ||
        (Date.parse(value.endedAt) >= start && Date.parse(value.endedAt) <= deadline))
    );
  }, 'Review sessions must fit within thirty minutes');

export type CardContent = z.infer<typeof cardContentSchema>;
export type LearningCard = z.infer<typeof learningCardSchema>;
export type LearningSession = z.infer<typeof learningSessionSchema>;
export type LearningRating = 'again' | 'hard' | 'good' | 'easy';
type Row = Pick<RecordData, 'id' | 'kind' | 'value'> & { deleted?: boolean };
export type CardRow = { id: string; value: LearningCard };

export function newLearningCard(
  content: CardContent,
  source: string,
  noteId: string | null,
  now = Date.now(),
): LearningCard {
  return learningCardSchema.parse({
    ...content,
    source,
    noteId,
    status: 'active',
    approvedAt: new Date(now).toISOString(),
    introducedAt: null,
    lastReviewedAt: null,
    dueAt: new Date(now).toISOString(),
    intervalDays: 0,
    ease: 2.5,
    reviews: 0,
    lapses: 0,
  });
}

/** A small, version-one interval scheduler; intervals describe recall, not mastery. */
export function rateLearningCard(
  card: LearningCard,
  rating: LearningRating,
  now = Date.now(),
  zone = calendarZone(),
): LearningCard {
  if (card.status !== 'active') {
    throw new Error('Resume this card before reviewing it.');
  }

  const again = rating === 'again';

  const ease = Math.min(
    3,
    Math.max(
      1.3,
      card.ease + (again ? -0.2 : rating === 'hard' ? -0.15 : rating === 'easy' ? 0.15 : 0),
    ),
  );

  const intervalDays = again
    ? 0
    : card.intervalDays < 1
      ? ({ hard: 1, good: 3, easy: 7 } as const)[rating]
      : Math.min(
          3650,
          Math.max(
            card.intervalDays + 1,
            Math.round(
              card.intervalDays * (rating === 'hard' ? 1.2 : rating === 'easy' ? ease * 1.3 : ease),
            ),
          ),
        );

  const instant = new Date(now).toISOString();

  return {
    ...card,
    ease,
    intervalDays,
    introducedAt: card.introducedAt ?? instant,
    lastReviewedAt: instant,
    dueAt: new Date(
      again
        ? now + 60_000
        : Temporal.Instant.fromEpochMilliseconds(now)
            .toZonedDateTimeISO(zone)
            .add({ days: intervalDays })
            .startOfDay().epochMilliseconds,
    ).toISOString(),
    reviews: card.reviews + 1,
    lapses: card.lapses + Number(again),
  };
}

export function learningCards(records: Row[]): CardRow[] {
  return records
    .filter(r => r.kind === 'learning-card' && !r.deleted)
    .map(r => ({ id: r.id, value: r.value as LearningCard }));
}

export function learningQueue(records: Row[], now = Date.now(), zone = calendarZone()) {
  const cards = learningCards(records),
    day = localDay(new Date(now), zone);

  const active = cards.filter(r => r.value.status === 'active');

  const reviews = active
    .filter(r => r.value.introducedAt && Date.parse(r.value.dueAt) <= now)
    .sort((a, b) => a.value.dueAt.localeCompare(b.value.dueAt) || a.id.localeCompare(b.id));

  const introduced = cards.filter(
    r => r.value.introducedAt && localDay(new Date(r.value.introducedAt), zone) === day,
  ).length;

  const waiting = active
    .filter(r => !r.value.introducedAt)
    .sort(
      (a, b) => a.value.approvedAt.localeCompare(b.value.approvedAt) || a.id.localeCompare(b.id),
    );

  // Five introductions at most; every ten currently due reviews reduces that by one.
  const newAllowance = Math.max(0, learningNewLimit - introduced - Math.floor(reviews.length / 10));

  return {
    reviews,
    waiting,
    introduced,
    newAllowance,
    queue: [...reviews, ...waiting.slice(0, newAllowance)],
  };
}

export function learningDay(records: Row[], now = Date.now(), zone = calendarZone()) {
  const day = localDay(new Date(now), zone);

  const sessions = records
    .filter(
      r => r.kind === 'learning-session' && !r.deleted && (r.value as LearningSession).day === day,
    )
    .map(r => ({ id: r.id, value: r.value as LearningSession }));

  // Merge overlapping sessions from devices, rather than counting the same minute twice.
  const ranges = sessions
    .map(({ value: s }) => [
      Date.parse(s.startedAt),
      Math.min(now, Date.parse(s.endedAt ?? s.deadline)),
    ])
    .filter(([start, end]) => end > start)
    .sort((a, b) => a[0] - b[0]);

  let spent = 0,
    until = 0;

  for (const [start, end] of ranges) {
    spent += Math.max(0, end - Math.max(start, until));
    until = Math.max(until, end);
  }

  const remainingMs = Math.max(0, learningLimitMs - spent);

  const active = sessions.find(
    ({ value: s }) => !s.endedAt && Date.parse(s.startedAt) <= now && Date.parse(s.deadline) > now,
  );

  return {
    day,
    remainingMs,
    active: remainingMs > 0 ? active : undefined,
    reviewed: sessions.reduce((sum, s) => sum + s.value.reviewed, 0),
  };
}
